import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useCreateProviderConfigMutation } from '../../../store/api/providerConfigsApi';

export default function AddProviderConfigPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [createProviderConfig, { isLoading: isCreating }] = useCreateProviderConfigMutation();

  // Form states
  const [providerId, setProviderId] = useState<string>('');
  const [apiKey, setApiKey] = useState<string>('');
  const [baseUrl, setBaseUrl] = useState<string>('');
  const [margins, setMargins] = useState<Array<{ key: string; value: string }>>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!providerId.trim()) {
      toast.error(t('admin.providerConfigs.add.error.providerIdRequired') || 'Provider ID is required');
      return;
    }

    if (!baseUrl.trim()) {
      toast.error(t('admin.providerConfigs.add.error.baseUrlRequired') || 'Base URL is required');
      return;
    }

    try {
      const marginsObj: Record<string, number> = {};
      margins.forEach(({ key, value }) => {
        const trimmedKey = key.trim();
        const numValue = parseFloat(value);
        if (trimmedKey && !isNaN(numValue) && numValue >= 0 && numValue <= 1000) {
          marginsObj[trimmedKey] = numValue / 100; // Convert to decimal
        }
      });

      await createProviderConfig({
        providerId: providerId.trim(),
        apiKeys: apiKey.trim() ? { apiKey: apiKey.trim() } : undefined,
        endpoints: { baseUrl: baseUrl.trim() },
        margins: Object.keys(marginsObj).length > 0 ? marginsObj : undefined,
      }).unwrap();

      toast.success(t('admin.providerConfigs.add.success') || 'Provider configuration created successfully');
      navigate(`/admin/provider-configs/${providerId.trim()}`, { replace: true });
    } catch (err) {
      const error = err as { data?: { error?: string }; message?: string };
      toast.error(error?.data?.error || error?.message || 'Failed to create provider configuration');
    }
  };

  const handleAddMargin = () => {
    setMargins([...margins, { key: '', value: '' }]);
  };

  const handleRemoveMargin = (index: number) => {
    setMargins(margins.filter((_, i) => i !== index));
  };

  const handleMarginKeyChange = (index: number, newKey: string) => {
    const newMargins = [...margins];
    const current = newMargins[index] ?? { key: '', value: '' };
    newMargins[index] = { ...current, key: newKey };
    setMargins(newMargins);
  };

  const handleMarginValueChange = (index: number, newValue: string) => {
    const newMargins = [...margins];
    const current = newMargins[index] ?? { key: '', value: '' };
    newMargins[index] = { ...current, value: newValue };
    setMargins(newMargins);
  };

  return (
    <div className="px-2 sm:px-4 lg:px-0">
      {/* Header */}
      <div
        className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between"
        style={{
          animation: 'fadeIn 0.5s ease-out',
        }}
      >
        <div>
          <Link
            to="/admin/provider-configs"
            className="text-brand-accent hover:text-brand-accent/80 font-semibold mb-4 inline-flex items-center gap-2 transition-all duration-200 hover:underline"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            {t('admin.providerConfigs.details.back') || 'Back to Provider Configs'}
          </Link>
          <h1 className="text-3xl font-heading font-bold text-brand-accent mb-2">
            {t('admin.providerConfigs.add.title') || 'Add New Provider'}
          </h1>
          <p className="text-neutral-medium">
            {t('admin.providerConfigs.add.subtitle') || 'Create a new provider configuration'}
          </p>
        </div>
      </div>

      {/* Form */}
      <div
        className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-6 border border-neutral-dark/50"
        style={{
          animation: 'slideUp 0.5s ease-out 0.1s both',
        }}
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Provider ID */}
          <div>
            <label htmlFor="providerId" className="block text-sm font-medium text-neutral-light mb-2">
              {t('admin.providerConfigs.add.providerId') || 'Provider ID'} *
            </label>
            <input
              id="providerId"
              type="text"
              value={providerId}
              onChange={(e) => setProviderId(e.target.value)}
              placeholder="smmworld"
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200"
              disabled={isCreating}
              required
            />
            <p className="mt-2 text-xs text-neutral-medium">
              {t('admin.providerConfigs.add.providerIdHint') || 'e.g., smmworld, smmcpan'}
            </p>
          </div>

          {/* Base URL */}
          <div>
            <label htmlFor="baseUrl" className="block text-sm font-medium text-neutral-light mb-2">
              {t('admin.providerConfigs.add.baseUrl') || 'Base URL'} *
            </label>
            <input
              id="baseUrl"
              type="text"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://smmworld.org/api/v2"
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200"
              disabled={isCreating}
              required
            />
            <p className="mt-2 text-xs text-neutral-medium">
              {t('admin.providerConfigs.add.baseUrlHint') || 'e.g., https://smmworld.org/api/v2'}
            </p>
          </div>

          {/* API Key */}
          <div>
            <label htmlFor="apiKey" className="block text-sm font-medium text-neutral-light mb-2">
              {t('admin.providerConfigs.add.apiKey') || 'API Key'}
            </label>
            <input
              id="apiKey"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={t('admin.providerConfigs.add.apiKeyPlaceholder') || 'Enter API key (optional)'}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200"
              disabled={isCreating}
            />
            <p className="mt-2 text-xs text-neutral-medium">
              {t('admin.providerConfigs.add.apiKeyHint') || 'API key will be encrypted automatically'}
            </p>
          </div>

          {/* Margins */}
          <div>
            <label className="block text-sm font-medium text-neutral-light mb-2">
              {t('admin.providerConfigs.add.margins') || 'Margins (%)'} ({t('common.optional') || 'Optional'})
            </label>
            <div className="space-y-2">
              {margins.map((margin, index) => (
                <div key={index} className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    value={margin.key}
                    onChange={(e) => handleMarginKeyChange(index, e.target.value)}
                    placeholder="social_en"
                    className="flex-1 px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200"
                    disabled={isCreating}
                  />
                  <div className="flex gap-2">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="1000"
                      value={margin.value}
                      onChange={(e) => handleMarginValueChange(index, e.target.value)}
                      placeholder="15.0"
                      className="w-full sm:w-24 px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200"
                      disabled={isCreating}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveMargin(index)}
                      disabled={isCreating}
                      className="px-3 sm:px-4 py-2 bg-red-500/20 text-red-400 border border-red-500/50 rounded-lg hover:bg-red-500/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
                    >
                      {t('admin.providerConfigs.details.info.removeMargin') || 'Remove'}
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={handleAddMargin}
                disabled={isCreating}
                className="px-4 py-2 bg-neutral-medium text-neutral-light rounded-lg hover:bg-neutral-medium/80 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {t('admin.providerConfigs.details.info.addMargin') || 'Add Margin'}
              </button>
            </div>
            <p className="mt-2 text-xs text-neutral-medium">
              {t('admin.providerConfigs.add.marginsHint') || 'Values from 0 to 1000 (0% to 1000%)'}
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex gap-3 pt-4">
            <Link
              to="/admin/provider-configs"
              className="flex-1 px-6 py-3 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-200 text-center"
            >
              {t('common.cancel') || 'Cancel'}
            </Link>
            <button
              type="submit"
              disabled={isCreating}
              className="flex-1 px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating ? t('common.saving') || 'Saving...' : t('common.save') || 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

